import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import MeasureWrapper from './MeasureWrapper';
import { SECTION_HEADER_PREFIX } from './constants';
import { defaultKeyExtractor } from './helpers';
import useVirtualization from './useVirtualization';

// ─── Flat Item Types ────────────────────────────────────────────────────────
//
// Each entry in the unified flat array carries a `_type` tag:
//
//   section-header  — the section's header row
//   item            — a regular data item within a section
//   section-empty   — rendered when a section's data array is empty;
//                     each section can supply its own ListEmptyComponent,
//                     falling back to the global one, then null
//
const FLAT_TYPE_HEADER = 'section-header';
const FLAT_TYPE_ITEM = 'item';
const FLAT_TYPE_EMPTY = 'section-empty';

const isSectionHeaderKey = key => typeof key === 'string' && key.startsWith(SECTION_HEADER_PREFIX);

const SectionListBase = (props, ref) => {
   const {
      sections = [],
      renderItem,
      renderSectionHeader,
      containerRef,
      itemHeight = 80,
      overscan,
      getItemHeight,
      keyExtractor = defaultKeyExtractor,
      onScroll,
      inverted = false,
      initialScrollKey = null,
      initialScrollIndex = null,
      initialScrollBlock = 'center',
      ItemSeparatorComponent = null,
      SectionSeparatorComponent = null,
      ListEmptyComponent = null, // global fallback when no section-level empty component
      enableVirtualization = true,
      virtualizationThreshold = 50,
   } = props;

   // ─── Flatten sections → flat items ──────────────────────────────────────
   //
   // For each section:
   //   1. Always push a section-header sentinel.
   //   2a. If data is non-empty  → push one flat item per data entry.
   //   2b. If data is empty      → push a single section-empty sentinel.
   //       The sentinel carries the section's own ListEmptyComponent (if any)
   //       and the global fallback so renderFlatItem can pick the right one
   //       without reaching back into props.

   const flatItems = useMemo(() => {
      const result = [];

      sections.forEach((section, sectionIndex) => {
         // ── Header ──────────────────────────────────────────────────────
         result.push({
            _type: FLAT_TYPE_HEADER,
            _key: `${SECTION_HEADER_PREFIX}${sectionIndex}`,
            section,
            sectionIndex,
         });

         const sectionData = section.data ?? [];

         if (sectionData.length === 0) {
            // ── Empty sentinel ───────────────────────────────────────────
            // Prefer section.ListEmptyComponent, fall back to global prop.
            result.push({
               _type: FLAT_TYPE_EMPTY,
               _key: `${SECTION_HEADER_PREFIX}${sectionIndex}-empty`,
               section,
               sectionIndex,
               emptyComponent: section.ListEmptyComponent ?? ListEmptyComponent,
            });
         } else {
            // ── Regular items ────────────────────────────────────────────
            sectionData.forEach((item, itemIndex) => {
               result.push({
                  _type: FLAT_TYPE_ITEM,
                  _key: keyExtractor(item, itemIndex),
                  item,
                  section,
                  sectionIndex,
                  originalIndex: itemIndex,
                  isLastInSection: itemIndex === sectionData.length - 1,
                  isLastSection: sectionIndex === sections.length - 1,
               });
            });
         }
      });

      return result;
   }, [sections, keyExtractor, ListEmptyComponent]);

   // ─── Rendering Mode ──────────────────────────────────────────────────────

   const renderingMode = useMemo(
      () => (enableVirtualization && flatItems.length >= virtualizationThreshold ? 'virtualized' : 'flat'),
      [enableVirtualization, flatItems.length, virtualizationThreshold],
   );

   // ─── Flat key extractor ───────────────────────────────────────────────────

   const flatKeyExtractor = useCallback(flatItem => flatItem._key, []);

   // ─── Core Hook ────────────────────────────────────────────────────────────

   const {
      visibleRange,
      isReady,
      offsetTop,
      offsetBottom,
      measureItem,
      scrollToKey: flatScrollToKey,
      scrollToIndex,
      jumpToBottom,
      takeScrollSnapshot,
      clearScrollSnapshot,
   } = useVirtualization({
      items: flatItems,
      containerRef,
      itemHeight,
      overscan,
      getItemHeight,
      keyExtractor: flatKeyExtractor,
      onScroll,
      inverted,
      initialScrollKey,
      initialScrollIndex,
      initialScrollBlock,
      isHeaderItem: isSectionHeaderKey,
   });

   const scrollToKey = useCallback((key, options = {}) => flatScrollToKey(key, options), [flatScrollToKey]);

   useImperativeHandle(
      ref,
      () => ({
         scrollToKey,
         scrollToIndex,
         jumpToBottom,
         scrollToBottomInstant: () => jumpToBottom({ smooth: false }),
         getScrollContainer: () => containerRef?.current,
         takeScrollSnapshot,
         clearScrollSnapshot,
      }),
      [scrollToKey, scrollToIndex, jumpToBottom, containerRef, takeScrollSnapshot, clearScrollSnapshot],
   );

   // ─── Single-item renderer ─────────────────────────────────────────────────

   const renderFlatItem = useCallback(
      (flatItem, flatIndex, { withMeasure = false } = {}) => {
         const key = flatItem._key;

         // ── Section header ───────────────────────────────────────────────
         if (flatItem._type === FLAT_TYPE_HEADER) {
            const showSectionSeparator = SectionSeparatorComponent && flatItem.sectionIndex > 0;

            const header = (
               <div id={key} data-key={key} data-index={flatIndex} className="section-header">
                  {renderSectionHeader?.({ section: flatItem.section })}
               </div>
            );

            return (
               <React.Fragment key={key}>
                  {showSectionSeparator && <>{SectionSeparatorComponent}</>}
                  {withMeasure ? (
                     <MeasureWrapper index={flatIndex} measureItem={measureItem}>
                        {header}
                     </MeasureWrapper>
                  ) : (
                     header
                  )}
               </React.Fragment>
            );
         }

         // ── Per-section empty state ──────────────────────────────────────
         // Rendered when a section has no data. The emptyComponent was resolved
         // at flatten-time (section.ListEmptyComponent → global → null) so
         // there is no prop-reaching here.
         if (flatItem._type === FLAT_TYPE_EMPTY) {
            if (!flatItem.emptyComponent) return null;

            const emptyNode = (
               <div id={key} data-key={key} data-index={flatIndex} className="section-empty">
                  {flatItem.emptyComponent}
               </div>
            );

            return (
               <React.Fragment key={key}>
                  {withMeasure ? (
                     <MeasureWrapper index={flatIndex} measureItem={measureItem}>
                        {emptyNode}
                     </MeasureWrapper>
                  ) : (
                     emptyNode
                  )}
               </React.Fragment>
            );
         }

         // ── Regular item ─────────────────────────────────────────────────
         const sectionRenderItem = flatItem.section.renderItem ?? renderItem;
         const content = (
            <React.Fragment key={key}>
               <div id={key} data-key={key} data-index={flatIndex} className="virtualized-item">
                  {sectionRenderItem({
                     item: flatItem.item,
                     index: flatItem.originalIndex,
                     section: flatItem.section,
                     isReady,
                  })}
               </div>
               {ItemSeparatorComponent && !flatItem.isLastInSection && <>{ItemSeparatorComponent}</>}
            </React.Fragment>
         );

         return withMeasure ? (
            <MeasureWrapper key={key} index={flatIndex} measureItem={measureItem}>
               {content}
            </MeasureWrapper>
         ) : (
            content
         );
      },
      [renderItem, renderSectionHeader, ItemSeparatorComponent, SectionSeparatorComponent, measureItem, isReady],
   );

   // ─── Render ───────────────────────────────────────────────────────────────
   //
   // If ALL sections are empty and there is no global ListEmptyComponent,
   // render nothing. If a global empty component exists it will already
   // appear via the section-empty sentinels in the flat array.

   const hasVisibleContent = flatItems.some(fi => fi._type !== FLAT_TYPE_EMPTY || fi.emptyComponent);
   if (!hasVisibleContent && flatItems.length === 0) return ListEmptyComponent ? <>{ListEmptyComponent}</> : null;

   if (renderingMode === 'flat') {
      return <>{flatItems.map((flatItem, index) => renderFlatItem(flatItem, index))}</>;
   }

   const { start, end } = visibleRange;

   return (
      <div
         style={{
            opacity: isReady ? 1 : 0,
            transition: 'opacity 0.15s ease-in',
            position: 'relative',
            ...(inverted ? { display: 'flex', flexDirection: 'column-reverse' } : {}),
         }}
      >
         {offsetTop > 0 && <div className="virtualized-spacer" style={{ height: offsetTop, flexShrink: 0 }} />}
         {flatItems
            .slice(start, end)
            .map((flatItem, offset) => renderFlatItem(flatItem, start + offset, { withMeasure: true }))}
         {offsetBottom > 0 && <div className="virtualized-spacer" style={{ height: offsetBottom, flexShrink: 0 }} />}
      </div>
   );
};

const SectionList = forwardRef(SectionListBase);

SectionListBase.propTypes = {
   sections: PropTypes.arrayOf(
      PropTypes.shape({
         title: PropTypes.string,
         data: PropTypes.array.isRequired,
         renderItem: PropTypes.func,
         // Per-section empty state — shown when data is [].
         // Falls back to the top-level ListEmptyComponent if omitted.
         ListEmptyComponent: PropTypes.node,
      }),
   ),
   renderItem: PropTypes.func,
   renderSectionHeader: PropTypes.func,
   containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
   itemHeight: PropTypes.number,
   overscan: PropTypes.number,
   getItemHeight: PropTypes.func,
   keyExtractor: PropTypes.func,
   onScroll: PropTypes.func,
   inverted: PropTypes.bool,
   initialScrollKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   initialScrollIndex: PropTypes.number,
   initialScrollBlock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   ItemSeparatorComponent: PropTypes.node,
   SectionSeparatorComponent: PropTypes.node,
   // Global fallback — used when a section has no ListEmptyComponent of its own
   ListEmptyComponent: PropTypes.node,
   enableVirtualization: PropTypes.bool,
   virtualizationThreshold: PropTypes.number,
};

export default SectionList;
